import type { ListAccessibilityLabelProps } from "../List";
import type { StrictSpacingStyle } from "../util/types";

import {
  RadioSelectorItem,
  type RadioSelectorItemAccessibilityLabelProps,
  type RadioSelectorItemProps,
  type RadioSelectorItemRef,
} from "./RadioSelectorItem";
import { Box } from "../Box";
import { ErrorText } from "../ErrorText";
import { Label } from "../Label";
import { useTheme, type Theme } from "../ThemeProvider";
import { VisuallyHidden } from "../VisuallyHidden";

import React, { useCallback, useId, useMemo, useRef } from "react";
import { Platform, StyleSheet, View } from "react-native";

export interface RadioGroupAccessibilityLabelProps extends Omit<
  ListAccessibilityLabelProps,
  "description"
> {
  /**
   * Announces ingress and egress of a radio group
   * @default "radio group"
   */
  group?: string;
}

export interface RadioSelectListProps {
  /**
   * Whether this input is disabled
   */
  disabled?: boolean;

  /**
   * Whether this input is required, will be applied to the label.
   */
  required?: boolean;

  /**
   * A11y: screen reader text announcing the radiogroup requires a selection
   * @default: "required"
   */
  requiredText?: string;

  /**
   * Whether this input is required, will be applied to the label.
   */
  invalid?: boolean;

  /**
   * A11y: screen reader text announcing the radiogroup is in an invalid state
   * @default: "invalid"
   */
  invalidText?: string;

  /**
   * the string contents that should populate the label above the selector list
   */
  labelText: string;

  /**
   * the string contents that should populate explainer text under the label
   */
  explainerText?: string;

  /**
   * the string contents that should populate under error text
   */
  errorText?: string;

  /**
   * the list of RadioSelectorItemProps necessary to create the list of RadioSelectorItems
   */
  radioSelectorProps: RadioSelectorItemProps[];

  /**
   * index of selected radio button
   */
  selectedIndex?: number;

  /*
   * Accessible labels to describe parts of the list
   */
  a11yLabels?: RadioGroupAccessibilityLabelProps;

  /**
   * callback function that received new index on selection
   *
   * @param selected index of newly selected radio option
   */
  onSelect: (selected: number) => void;
}

/**
 * Get the style overrides needed for first and last list items to be padded correctly
 */
function getStyleModifier(idx: number, length: number, theme: Theme): StrictSpacingStyle {
  const styleModifiers: StrictSpacingStyle = {};

  if (idx === 0) {
    styleModifiers.paddingTop = theme.spacing.s5;
  }

  if (idx === length - 1) {
    styleModifiers.paddingBottom = theme.spacing.s5;
  }

  return styleModifiers;
}

/**
 * @see https://www.figma.com/design/UQ0baUglOtMCWJ1jkwcSzW/Mobile-Apps-Design-System--MADS-?node-id=7046-5&p=f&t=I3sPQ925rDclD2Yy-0
 */
export function RadioSelectorList({
  disabled,
  required,
  requiredText,
  invalid,
  invalidText,
  labelText,
  explainerText,
  errorText,
  radioSelectorProps,
  selectedIndex,
  a11yLabels,
  onSelect,
}: RadioSelectListProps) {
  const theme = useTheme();
  const styles = useMemo(
    () =>
      StyleSheet.create({
        container: {
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch",
          gap: theme.spacing.s2,
          borderColor: theme.colors.borderWeak,
        },
        selectorList: {
          backgroundColor: theme.colors.background,
          borderColor: theme.colors.borderWeak,
          borderWidth: 1,
          padding: 1,
          borderRadius: theme.borderRadius.lg,
          overflow: "hidden",
        },
        disabled: {
          opacity: 0.4,
        },
        error: {
          borderColor: theme.colors.borderStatusCriticalStrong,
        },
      }),
    [theme],
  );

  const radioGroupId = useId();
  const labelId = `MADS_RadioGroup_label_${radioGroupId}`;
  const errorId = `MADS_RadioGroup_error_${radioGroupId}`;
  const isInvalid = invalid ?? !!errorText; // Makes 'invalid' consistent with other MADS controls
  const { positionText = "${pos} of ${len}", group = "radio group" } = a11yLabels ?? {};
  const radioSelectorItemA11yLabels: RadioSelectorItemAccessibilityLabelProps = {
    errorText,
    invalidText,
    radioGroupDescription: labelText,
    requiredText,
  };

  const itemRefs = useRef<(RadioSelectorItemRef | null)[]>([]);
  const count = radioSelectorProps.length;

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (disabled || !count) return;

      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
      const key = (e.nativeEvent as unknown as KeyboardEvent).key;
      const currentIndex = selectedIndex ?? 0;

      if (key === " ") {
        e.preventDefault?.();
        onSelect(currentIndex);
        return;
      }

      let nextIndex: number | undefined;
      if (key === "ArrowDown" || key === "ArrowRight") {
        e.preventDefault?.();
        nextIndex = (currentIndex + 1) % count;
      } else if (key === "ArrowUp" || key === "ArrowLeft") {
        e.preventDefault?.();
        nextIndex = (currentIndex - 1 + count) % count;
      }

      if (typeof nextIndex !== "number") return;
      const nextRef = itemRefs.current[nextIndex];

      if (nextRef && typeof nextRef.focus === "function") {
        nextRef.focus();
      }
      onSelect(nextIndex);
    },
    [count, disabled, onSelect, selectedIndex],
  );

  return (
    <Box sx={[styles.container, errorText && styles.error]}>
      <Label
        text={labelText}
        labelID={labelId}
        explainerText={explainerText}
        isRequired={required}
        requiredText={requiredText}
        isInvalid={isInvalid}
        invalidText={invalidText}
        renderStatesToScreenReader={true}
      />

      <VisuallyHidden>{group}</VisuallyHidden>
      <Box
        sx={[styles.selectorList, errorText && styles.error, disabled && styles.disabled]}
        role="radiogroup"
        accessibilityRole="radiogroup"
        accessibilityLabelledBy={labelId}
        {...(Platform.OS === "web" && {
          onKeyDown: handleKeyDown,
        })}
      >
        {radioSelectorProps.map((radio, idx) => (
          <RadioSelectorItem
            key={idx}
            ref={(el) => {
              itemRefs.current[idx] = el;
            }}
            id={idx}
            styleModifier={getStyleModifier(idx, radioSelectorProps.length, theme)}
            isFirstItem={idx === 0}
            isLastItem={idx === radioSelectorProps.length - 1}
            labelText={radio.labelText}
            explainerText={radio.explainerText}
            testID={radio.testID}
            selected={idx === selectedIndex}
            onSelect={onSelect}
            disabled={disabled}
            error={isInvalid}
            required={required}
            a11yLabels={{
              ...radioSelectorItemA11yLabels,
              positionText: positionText
                .replaceAll("${pos}", (idx + 1).toString())
                .replaceAll("${len}", radioSelectorProps.length.toString()),
            }}
            {...(Platform.OS === "web" && {
              tabIndex:
                selectedIndex !== undefined && selectedIndex !== null
                  ? selectedIndex === idx
                    ? 0
                    : -1
                  : idx === 0
                    ? 0
                    : -1,
            })}
            {...(errorText && { "aria-describedby": errorId })}
          />
        ))}
      </Box>

      <View style={[disabled && styles.disabled]}>
        {errorText && <ErrorText nativeID={errorId}>{errorText}</ErrorText>}
      </View>
    </Box>
  );
}
